import { PrimaryButton } from "@components/Buttons";
import { useTheme } from "@hooks/useTheme";
import { downloadFile, uppercase } from "@utils/common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEscapeKey from "@hooks/useEscapeKey";

const AnnouncementsModal = ({ isOpen, onClose, announcements = [], "data-testid": dataTestId = "announcements-modal" }) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();
  // State to track hidden announcement IDs
  const [hiddenAnnouncements, setHiddenAnnouncements] = useState(new Set());

  // Function to hide an announcement
  const hideAnnouncement = (announcementId) => {
    setHiddenAnnouncements((prev) => new Set([...prev, announcementId]));
  };

  // Filter out hidden announcements
  const visibleAnnouncements = announcements.filter(
    (announcement) => !hiddenAnnouncements.has(announcement.bulletinSeq),
  );

  const getDownloadLink = (fileInfoSeq) => `/download/${fileInfoSeq}`;

  // Function to handle file download
  const handleDownload = (fileInfo) => {
    downloadFile(fileInfo.realFileName || fileInfo.serverFileName, getDownloadLink(fileInfo.fileInfoSeq));
  };

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "---";
    const date = new Date(dateTimeString);
    const currentLang = i18n.language;
    const locale = currentLang || "en";
    const month = date.toLocaleDateString(locale, { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();

    const time = date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${month} ${day}, ${year} ${time}`;
  };

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`overflow-y-auto fixed inset-0 z-50 flex items-center justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px]`}
    >
      <div
        data-testid={dataTestId}
        className={`relative w-[62.5rem] max-h-[53rem] ${colors.bgModal} transform overflow-hidden rounded-2xl shadow-xl transition-all duration-200`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-start max-h-[4.6875rem] px-6 pt-6`}
          data-testid={`${dataTestId}-header`}
        >
          <h3 className={`text-lg font-semibold ${colors.textPrimary}`} data-testid={`${dataTestId}-title`}>
            {uppercase(t("navbar.announcement"))}
          </h3>
        </div>

        {/* Content with Scroll */}
        <div className="max-h-[43.5rem] overflow-y-auto p-6" data-testid={`${dataTestId}-content`}>
          {visibleAnnouncements.length === 0 ? (
            // Message when all announcements are closed
            <div className="flex items-center justify-center h-full" data-testid={`${dataTestId}-empty`}>
              <div className={`text-center ${colors.textSecondary}`} data-testid={fieldValueTestId("empty-message")}>{t("bulletin.no-announcements")}</div>
            </div>
          ) : (
            <div className="space-y-3" data-testid={`${dataTestId}-list`}>
              {visibleAnnouncements.map((announcement) => (
                <div
                  key={announcement.bulletinSeq}
                  data-testid={`${dataTestId}-item-${announcement.bulletinSeq}`}
                  className={`w-full p-6 rounded-2xl ${colors.bgSecondary} transition-colors duration-200 flex flex-col`}
                >
                  {/* Header Section */}
                  <div className="mb-2" data-testid={`${dataTestId}-item-${announcement.bulletinSeq}-header`}>
                    {/* Title and Close Button Row */}
                    <div className="flex items-start justify-between mb-2">
                      <div
                        className={`text-lg font-bold ${colors.textPrimary} flex-1`}
                        data-testid={`${dataTestId}-item-${announcement.bulletinSeq}-title`}
                      >
                        {announcement.title || "No Title"}
                      </div>
                      {visibleAnnouncements.length > 1 && (
                        <button
                          type="button"
                          data-testid={`${dataTestId}-item-${announcement.bulletinSeq}-hide-button`}
                          onClick={(e) => {
                            hideAnnouncement(announcement.bulletinSeq);
                          }}
                          className={`ml-4 p-1 rounded-lg transition-colors duration-200 flex-shrink-0`}
                          aria-label="Close announcement"
                        >
                          <svg
                            className={`w-5 h-5 ${colors.textPrimary}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Timestamp */}
                    <div
                      className={`text-sm ${colors.textSecondary} mb-6`}
                      data-testid={`${dataTestId}-item-${announcement.bulletinSeq}-timestamp`}
                    >
                      {formatDateTime(announcement.createdAt)}
                    </div>

                    {/* Dashed Line */}
                    <div className={`border-t border-dashed ${colors.borderPrimary} mb-4`}></div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-1" data-testid={`${dataTestId}-item-${announcement.bulletinSeq}-body`}>
                    <div
                      className={`text-sm ${colors.textSecondary} leading-relaxed break-words whitespace-pre-wrap`}
                      data-testid={`${dataTestId}-item-${announcement.bulletinSeq}-text`}
                    >
                      {announcement.content || "No content available"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end border-t ${colors.borderPrimary} p-6`}
          data-testid={`${dataTestId}-footer`}
        >
          <PrimaryButton onClick={onClose} className="px-6 py-1.5" data-testid={`${dataTestId}-ok-button`}>
            {t("common.ok-button")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsModal;
